import React, { useState, useEffect } from 'react';
import {
    Users, UserPlus, Mail, MoreVertical, Crown, Shield, Calendar,
    AlertCircle, Loader2, Edit2, RefreshCw, Trash2, X, Fingerprint, ShieldCheck, Terminal,
} from 'lucide-react';
import { Input } from '@/components/common/ui/Input';
import { Table, Column, SortDirection, TableCell, TableRow } from '@/components/common/ui/Table';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator
} from '@/components/common/ui/DropdownMenu';
import {
    useGetOrganizationUsersQuery,
    useInviteUserMutation,
    useUpdateUserRoleMutation,
    useRemoveUserMutation,
    useResendInviteMutation,
} from '@/lib/services/organizationApi';
import { useToast } from '@/hooks/components/common/toast/useToast';
import type { OrganizationUser, RoleType, } from '@/lib/api/types';
import ConfirmationModal from '@/components/common/ui/ConfirmationModal';
import { Button } from '@/components/common/ui/Button';

// --- Cyber Badge Components ---

const RoleBadge: React.FC<{ role: RoleType }> = ({ role }) => {
    const config = role === 'admin'
        ? { border: 'border-[#A855F7]/40 text-[#A855F7] bg-[#A855F7]/5', icon: <Crown className="h-3 w-3" />, label: 'ROOT_ADMIN' }
        : { border: 'border-[#00FFFF]/40 text-[#00FFFF] bg-[#00FFFF]/5', icon: <Shield className="h-3 w-3" />, label: 'OPERATOR' };

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-widest ${config.border}`}>
            {config.icon}
            {config.label}
        </span>
    );
};

const StatusBadge: React.FC<{ status?: string }> = ({ status = 'active' }) => {
    const configs: Record<string, string> = {
        active: 'border-[#50A970]/40 text-[#50A970] bg-[#50A970]/5',
        pending: 'border-[#EAB308]/40 text-[#EAB308] bg-[#EAB308]/5',
        suspended: 'border-[#FF3333]/40 text-[#FF3333] bg-[#FF3333]/5',
    };
    const currentStatus = status.toLowerCase();
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[9px] font-bold uppercase tracking-widest ${configs[currentStatus] || configs.active}`}>
            {status}
        </span>
    );
};

const UserDisplay: React.FC<{ user: OrganizationUser }> = ({ user }) => {
    const name = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email?.split('@')[0] || 'UNK_USER';
    return (
        <div className="flex items-center gap-3 font-mono">
            <div className="w-9 h-9 bg-[#12151C] border border-[#1A1D24] rounded-lg flex items-center justify-center text-[#00FFFF] text-xs font-bold shadow-inner">
                {name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
                <div className="text-[12px] font-bold text-white uppercase tracking-tight truncate">{name}</div>
                <div className="text-[10px] text-slate-500 lowercase tracking-tight truncate">{user.email}</div>
            </div>
        </div>
    );
};

const DateDisplay: React.FC<{ date?: string | null }> = ({ date }) => {
    if (!date) return <span className="text-slate-600 font-mono text-[10px]">---</span>;
    return (
        <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono uppercase">
            <Calendar className="h-3 w-3 opacity-30" />
            {new Date(date).toLocaleDateString()}
        </div>
    );
};

type SortField = 'name' | 'email' | 'role' | 'status' | 'joined_at' | 'last_active';

const OrganizationUsers: React.FC = () => {
    const toast = useToast();

    // API Hooks
    const { data: users, isLoading: usersLoading, isError: usersError, refetch: refetchUsers } = useGetOrganizationUsersQuery();
    const [inviteUser, { isLoading: inviteLoading, isSuccess: inviteSuccess, error: inviteErrorDetails, isError: inviteError }] = useInviteUserMutation();
    const [updateUserRole, { isLoading: updateRoleLoading, isSuccess: updateRoleSuccess, error: updateRoleErrorDetails, isError: updateRoleError }] = useUpdateUserRoleMutation();
    const [removeUser, { isLoading: removeUserLoading, isSuccess: removeUserSuccess, error: removeUserErrorDetails, isError: removeUserError }] = useRemoveUserMutation();
    const [resendInvite, { isSuccess: resendInviteSuccess, isError: resendInviteError, error: resendInviteErrorDetails }] = useResendInviteMutation();

    // Local State
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<RoleType>('member');
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [sortField, setSortField] = useState<SortField>('joined_at');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const [editUserModal, setEditUserModal] = useState<{ isOpen: boolean; user: OrganizationUser | null; selectedRole: RoleType }>({
        isOpen: false, user: null, selectedRole: 'member'
    });
    const [removeUserModal, setRemoveUserModal] = useState<{ isOpen: boolean; user: OrganizationUser | null }>({
        isOpen: false, user: null
    });

    const getErrorMessage = (error: unknown): string => {
        if (!error) return '';
        if (typeof error === 'string') return error;
        if (error && typeof error === 'object') {
            if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
                return String((error.data as any).message);
            }
            if ('message' in error) return String((error as any).message);
        }
        return 'AN_UNEXPECTED_INTERRUPT_OCCURRED';
    };

    // --- Success/Error Handlers ---

    useEffect(() => {
        if (inviteSuccess) {
            setInviteEmail(''); setShowInviteForm(false); refetchUsers();
            toast({ type: 'success', title: 'NODE_INVITED', message: 'Deployment authorization sent successfully.' });
        }
        if (inviteError) {
            toast({ type: 'error', title: 'INVITE_FAILED', message: getErrorMessage(inviteErrorDetails) });
        }
    }, [inviteSuccess, inviteError]);

    useEffect(() => {
        if (updateRoleSuccess) {
            setEditUserModal({ isOpen: false, user: null, selectedRole: 'member' }); refetchUsers();
            toast({ type: 'success', title: 'PERMISSIONS_SYNCED', message: 'User clearance level modified.' });
        }
        if (updateRoleError) {
            toast({ type: 'error', title: 'UPDATE_FAILED', message: getErrorMessage(updateRoleErrorDetails) });
        }
    }, [updateRoleSuccess, updateRoleError]);

    useEffect(() => {
        if (removeUserSuccess) {
            setRemoveUserModal({ isOpen: false, user: null }); refetchUsers();
            toast({ type: 'success', title: 'NODE_SEVERED', message: 'User access revoked successfully.' });
        }
        if (removeUserError) {
            toast({ type: 'error', title: 'REMOVE_FAILED', message: getErrorMessage(removeUserErrorDetails) });
        }
    }, [removeUserSuccess, removeUserError]);

    useEffect(() => {
        if (resendInviteSuccess) {
            toast({ type: 'success', title: 'AUTH_RESENT', message: 'Invitation hash re-dispatched.' });
        }
        if (resendInviteError) {
            toast({ type: 'error', title: 'RESEND_FAILED', message: getErrorMessage(resendInviteErrorDetails) });
        }
    }, [resendInviteSuccess, resendInviteError]);

    // --- Logic Handlers ---

    const handleInviteUser = async () => {
        if (!inviteEmail.trim()) return;
        try { await inviteUser({ email: inviteEmail.trim(), role: inviteRole }).unwrap(); } catch (e) { }
    };

    const handleEditUser = (user: OrganizationUser) => {
        if (!user.external_user_id) {
            toast({ type: 'error', title: 'INVALID_NODE', message: 'Identifier missing.' });
            return;
        }
        setEditUserModal({ isOpen: true, user, selectedRole: user.role });
    };

    const handleConfirmEditUser = async () => {
        if (!editUserModal.user?.external_user_id) return;
        if (editUserModal.selectedRole === editUserModal.user.role) {
            setEditUserModal({ isOpen: false, user: null, selectedRole: 'member' });
            return;
        }
        try { await updateUserRole({ external_user_id: editUserModal.user.external_user_id, role: editUserModal.selectedRole }).unwrap(); } catch (e) { }
    };

    const handleResendInvite = async (user: OrganizationUser) => {
        if (!user.email) return;
        try { await resendInvite({ email: user.email, role: user.role || 'member' }).unwrap(); } catch (e) { }
    };

    const handleRemoveUser = (user: OrganizationUser) => {
        if (!user.external_user_id) return;
        setRemoveUserModal({ isOpen: true, user });
    };

    const handleConfirmRemoveUser = async () => {
        if (!removeUserModal.user?.external_user_id) return;
        try { await removeUser({ external_user_id: removeUserModal.user.external_user_id }).unwrap(); } catch (e) { }
    };

    if (usersLoading) {
        return (
            <div className="p-12 flex flex-col items-center justify-center font-mono gap-4">
                <RefreshCw className="h-8 w-8 animate-spin text-[#00FFFF]" />
                <span className="text-[10px] text-slate-500 uppercase tracking-[0.3em]">Querying_User_Matrix...</span>
            </div>
        );
    }

    if (usersError) {
        return (
            <div className="p-8 font-mono">
                <div className="bg-[#FF3333]/5 border border-[#FF3333]/20 rounded-xl p-8 text-center space-y-4">
                    <AlertCircle className="h-10 w-10 text-[#FF3333] mx-auto" />
                    <h3 className="text-white font-bold uppercase tracking-widest">Access_Denied</h3>
                    <p className="text-[11px] text-slate-500 uppercase">{getErrorMessage(usersError)}</p>
                    <button onClick={() => refetchUsers()} className="px-6 py-2 bg-transparent border border-[#FF3333] text-[#FF3333] hover:bg-[#FF3333] hover:text-white transition-all text-[10px] font-black uppercase tracking-widest rounded-lg">
                        Retry_Handshake
                    </button>
                </div>
            </div>
        );
    }

    const usersList = users || [];
    const activeUsers = usersList.filter(u => (u.status || 'active').toLowerCase() === 'active').length;
    const pendingUsers = usersList.filter(u => (u.status || 'active').toLowerCase() === 'pending').length;

    const indeterminate = selectedUsers.length > 0 && selectedUsers.length < usersList.length;

    const columns: Column[] = [
        { id: 'name', label: 'User', sortable: true, minWidth: '250px' },
        { id: 'role', label: 'Role', sortable: true, width: '140px' },
        { id: 'status', label: 'Status', sortable: true, width: '120px' },
        { id: 'joined_at', label: 'Joined', sortable: true, width: '140px' },
        { id: 'last_active', label: 'Last Active', sortable: true, width: '140px' },
        { id: 'actions', label: 'Actions', width: '80px' }
    ];

    return (
        <div className="p-8 space-y-8 font-mono animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Fingerprint className="h-5 w-5 text-[#00FFFF]" />
                    <h3 className="text-[14px] font-bold text-white uppercase tracking-[0.2em]">
                        Organization Users
                    </h3>
                </div>

                <button
                    onClick={() => setShowInviteForm(true)}
                    disabled={inviteLoading}
                    className="bg-[#A855F7] text-white font-black h-9 px-6 rounded-lg text-[10px] tracking-widest shadow-[0_0_15px_rgba(168,85,247,0.2)] hover:scale-105 active:scale-95 transition-all disabled:opacity-50 flex items-center gap-2"
                >
                    {inviteLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
                    Invite User
                </button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                    { label: 'Total Users', val: usersList.length, color: '#3B82F6', icon: Users },
                    { label: 'Active Users', val: activeUsers, color: '#50A970', icon: ShieldCheck },
                    { label: 'Pending Invites', val: pendingUsers, color: '#EAB308', icon: Mail }
                ].map((stat, i) => (
                    <div key={i} className="bg-[#050505] border border-[#1A1D24] rounded-xl p-5 flex items-center justify-between group hover:border-slate-700 transition-colors shadow-inner border-t-2" style={{ borderTopColor: stat.color }}>
                        <div>
                            <p className="text-slate-500 text-[9px] font-bold uppercase tracking-widest mb-1">{stat.label}</p>
                            <h3 className="text-xl font-bold text-white tracking-tight">{stat.val}</h3>
                        </div>
                        <stat.icon className="w-5 h-5 opacity-20 group-hover:opacity-100 transition-opacity" style={{ color: stat.color }} />
                    </div>
                ))}
            </div>

            {/* Invite Form */}
            {showInviteForm && (
                <div className="bg-[#0A0B0E] border border-[#A855F7]/30 rounded-xl p-6 shadow-[0_0_30px_rgba(168,85,247,0.05)] animate-in fade-in slide-in-from-top-2">
                    <h4 className="text-[11px] font-bold text-[#A855F7] uppercase tracking-widest mb-6 flex items-center gap-2">
                        <Terminal className="w-3.5 h-3.5" /> Initialize_Recruitment_Protocol
                    </h4>
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <Input
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="TARGET_EMAIL@SECURE.INTERNAL"
                                className="bg-black border-[#1A1D24] text-[#A855F7] h-11 text-[12px] placeholder:text-slate-800 uppercase focus:border-[#A855F7]"
                                disabled={inviteLoading}
                            />
                        </div>
                        <div className="w-full md:w-48">
                            <select
                                value={inviteRole}
                                onChange={(e) => setInviteRole(e.target.value as RoleType)}
                                className="w-full h-11 px-4 bg-black border border-[#1A1D24] text-slate-300 rounded-lg text-[11px] font-bold uppercase tracking-widest focus:border-[#A855F7] outline-none"
                                disabled={inviteLoading}
                            >
                                <option value="member">Operator</option>
                                <option value="admin">Root_Admin</option>
                            </select>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                onClick={handleInviteUser}
                                disabled={!inviteEmail.trim() || inviteLoading}
                                className="bg-[#A855F7] text-white px-8 font-black h-11 text-[10px] tracking-widest shadow-[0_0_15px_rgba(168,85,247,0.2)]"
                            >
                                EXECUTE
                            </Button>
                            <button
                                onClick={() => { setShowInviteForm(false); setInviteEmail(''); }}
                                className="px-4 text-[10px] font-bold text-slate-600 hover:text-white uppercase tracking-widest transition-colors"
                            >
                                ABORT
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Table Section */}
            <div className="bg-[#050505] border border-[#1A1D24] rounded-2xl overflow-hidden shadow-2xl">
                <Table
                    columns={columns}
                    onSort={(c, d) => { setSortField(c as SortField); setSortDirection(d); }}
                    sortColumn={sortField}
                    sortDirection={sortDirection}
                    selectable
                    onSelectAll={(c) => setSelectedUsers(c ? usersList.map(u => u.id.toString()) : [])}
                    allRowsSelected={usersList.length > 0 && selectedUsers.length === usersList.length}
                    indeterminate={indeterminate}
                >
                    {usersList.length === 0 ? (
                        <TableRow>
                            <TableCell className="text-center py-24 italic text-slate-700 uppercase tracking-[0.4em] text-[10px]">
                                Zero_Personnel_Detected
                            </TableCell>
                        </TableRow>
                    ) : (
                        usersList.map((user) => (
                            <TableRow
                                key={user.id}
                                selected={selectedUsers.includes(user.id.toString())}
                                onSelect={(c) => setSelectedUsers(prev => c ? [...prev, user.id.toString()] : prev.filter(id => id !== user.id.toString()))}
                            >
                                <TableCell><UserDisplay user={user} /></TableCell>
                                <TableCell><RoleBadge role={user.role} /></TableCell>
                                <TableCell><StatusBadge status={user.status} /></TableCell>
                                <TableCell><DateDisplay date={user.joined_at} /></TableCell>
                                <TableCell><DateDisplay date={user.last_active} /></TableCell>
                                <TableCell>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger className="p-2 text-slate-600 hover:text-white hover:bg-[#1A1D24] rounded-lg transition-colors outline-none border border-transparent hover:border-[#1A1D24]">
                                            <MoreVertical className="h-4 w-4" />
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="bg-[#0A0B0E] border-[#1A1D24] shadow-2xl font-mono p-1.5 min-w-[160px]">
                                            <DropdownMenuItem onClick={() => handleEditUser(user)} className="focus:bg-[#12151C] focus:text-[#00FFFF] text-[10px] font-bold uppercase tracking-wider cursor-pointer py-2.5">
                                                <Edit2 className="h-3.5 w-3.5 mr-2 opacity-50" /> Edit Role
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => handleResendInvite(user)} className="focus:bg-[#12151C] focus:text-[#EAB308] text-[10px] font-bold uppercase tracking-wider cursor-pointer py-2.5">
                                                <RefreshCw className="h-3.5 w-3.5 mr-2 opacity-50" /> Resend Invite
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator className="bg-[#1A1D24] my-1" />
                                            <DropdownMenuItem onClick={() => handleRemoveUser(user)} className="focus:bg-[#FF3333]/10 focus:text-[#FF3333] text-[10px] font-bold uppercase tracking-wider cursor-pointer py-2.5" destructive>
                                                <Trash2 className="h-3.5 w-3.5 mr-2 opacity-50" /> Remove User
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </Table>
            </div>

            {/* Edit Role Modal */}
            {editUserModal.isOpen && editUserModal.user && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-2xl w-full max-w-md shadow-[0_0_60px_rgba(0,0,0,0.9)] overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-[#1A1D24] bg-[#12151C] flex items-center justify-between">
                            <h3 className="text-[12px] font-bold text-white uppercase tracking-widest flex items-center gap-2">
                                <Edit2 className="w-4 h-4 text-[#00FFFF]" /> Node_Modification
                            </h3>
                            <button onClick={() => setEditUserModal({ isOpen: false, user: null, selectedRole: 'member' })} className="text-slate-600 hover:text-white transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-8 space-y-6">
                            <div className="text-center bg-black/40 border border-[#1A1D24] py-4 rounded-xl">
                                <p className="text-[9px] text-slate-600 uppercase tracking-widest mb-1">Target_Subject</p>
                                <p className="text-[13px] font-bold text-[#00FFFF] truncate px-4 lowercase">{editUserModal.user.email}</p>
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Assign_Clearance_Level</label>
                                <div className="grid grid-cols-2 gap-3">
                                    {(['member', 'admin'] as RoleType[]).map((r) => (
                                        <button
                                            key={r}
                                            onClick={() => setEditUserModal(prev => ({ ...prev, selectedRole: r }))}
                                            className={`py-3 rounded-lg border text-[10px] font-black uppercase tracking-[0.2em] transition-all ${editUserModal.selectedRole === r
                                                ? 'border-[#00FFFF] bg-[#00FFFF]/10 text-white shadow-[0_0_15px_rgba(0,255,255,0.1)]'
                                                : 'border-[#1A1D24] bg-black text-slate-600 hover:border-slate-700'
                                                }`}
                                        >
                                            {r === 'admin' ? 'Root_Admin' : 'Operator'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="p-6 bg-[#050505] border-t border-[#1A1D24] flex justify-end gap-3">
                            <button onClick={() => setEditUserModal({ isOpen: false, user: null, selectedRole: 'member' })} className="px-5 py-2 text-[10px] font-bold text-slate-600 hover:text-white uppercase tracking-widest transition-colors">Abort</button>
                            <Button onClick={handleConfirmEditUser} loading={updateRoleLoading} className="bg-[#00FFFF] text-black font-black px-6 text-[10px] tracking-widest">COMMIT_OVERRIDE</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove User Confirmation */}
            <ConfirmationModal
                isOpen={removeUserModal.isOpen}
                onClose={() => setRemoveUserModal({ isOpen: false, user: null })}
                onConfirm={handleConfirmRemoveUser}
                title="SEVER_NODE_CONNECTION"
                description={`Warning: De-authorizing ${removeUserModal.user?.email || 'this node'} will immediately terminate system access.`}
                confirmText="TERMINATE_ACCESS"
                cancelText="ABORT_COMMAND"
                variant="danger"
                loading={removeUserLoading}
            />
        </div>
    );
};

export default OrganizationUsers;